// src/components/layout/Sidebar.jsx
//
// The floating maroon card from the design: 196px wide at (20, 0), 940px tall, radius
// 28, logo on top, menu, then the lotus line-art and tagline at the foot. Geometry,
// colours and type are the design's own values.
//
// `drawer` (tablets / phones, see MainLayout): the same menu as a full-height panel that
// slides in from the left while `open`; the lotus art and tagline are left out there.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, Users, FolderTree, ChartCandlestick, NotebookPen, Logs, Settings,
  FileText, SquareCheckBig, ContactRound, MessageSquareCode, Landmark, ClockAlert,
  BadgeDollarSign, Hourglass, CirclePower, PackageSearch, Link2, BookOpenCheck,
  UsersRound, ListTodo, MessageCircle, FolderOpen, CalendarDays, Tag, Ticket,
  HelpingHand, UserRound, X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { TooltipProvider } from '../ui/tooltip';
import BrandLogo from 'assets/brand-logo.png';
import { ProtectedSidebarItem } from '../common/ProtectedSidebarItem';
import { useHasPermission } from '../../hooks/useHasPermission';

const AnimatedIcon = ({ icon: Icon, className = '' }) => (
  <Icon className={cn('w-5 h-5 shrink-0 transition-transform duration-300 group-hover:scale-110', className)} />
);

const Sidebar = ({ drawer = false, open = false, onClose }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openReportsSubmenu, setOpenReportsSubmenu] = useState(null);
  const hasPermission = useHasPermission();

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard', tooltip: 'Dashboard Overview', resource: 'dashboard' },
    { name: 'My Tasks', icon: MessageSquareCode, resource: 'to_do_list', path: '/to-do-list', tooltip: 'My Tasks' },
    { name: 'My Teams', icon: UserRound, path: '/my-teams', tooltip: 'Teams Overview' },
    {
      name: 'Sample Design', icon: SquareCheckBig, isDropdown: true, tooltip: 'Sample Design',
      children: [
        { name: 'Sample Designing', icon: SquareCheckBig, path: '/work-task-assignment', resource: 'work_assign_task', tooltip: 'Sample Designing' },
        { name: 'Design Masters', icon: FolderTree, path: '/design-masters', resource: 'design_master', tooltip: 'Sampling Design masters' },
        { name: 'Sample Designing Dashboard', icon: ChartCandlestick, path: '/designing-dashboard', resource: 'work_assign_dashboard', tooltip: 'Sample Designing Dashboard' },
        { name: 'Catalog Design', icon: FolderTree, path: '/catalog-design', resource: 'designed_catalog', tooltip: 'Designs that completed the Catalog stage' },
        { name: 'Work Assign Report', icon: HelpingHand, path: '/reports/work-task-report', tooltip: 'Work Assign Report', resource: 'reports' },
        { name: 'Target Achievement', icon: HelpingHand, path: '/reports/target-achievement', tooltip: 'Employee Target Achievement Report', resource: 'reports' },
        { name: 'Design-wise Time Sheet', icon: HelpingHand, path: '/reports/design-timesheet', tooltip: 'Work time per stage for every design', resource: 'reports' },
        { name: 'Design Details Report', icon: HelpingHand, path: '/reports/design-details', tooltip: 'Every design with its masters, costs and total time', resource: 'reports' },
        { name: 'Employee Time by Occasion', icon: HelpingHand, path: '/reports/employee-occasion-time', tooltip: 'Work time per employee, occasion-wise, with averages', resource: 'reports' },
      ],
    },
    {
      name: 'Manage Employee', icon: Users, isDropdown: true, tooltip: 'Employee Management',
      children: [
        { name: 'Employee', path: '/employee', tooltip: 'Manage Employees', resource: 'employee' },
        { name: 'Department', path: '/department', tooltip: 'Manage Departments', resource: 'department' },
        { name: 'Role', path: '/role', tooltip: 'Manage Roles', resource: 'role' },
        { name: 'Company', path: '/company', tooltip: 'Manage Companies', resource: 'company' },
        { name: 'Location', path: '/locations', tooltip: 'Manage Location', resource: 'employee_location' },
        { name: 'Leave Management', icon: CirclePower, resource: 'leave_management', path: '/leave-management', tooltip: 'Leave Management' },
        { name: 'Leave Policy', icon: CirclePower, path: '/leave-policy', resource: 'leave_policy', tooltip: 'Leave Policy' },
        { name: 'Hourly Work log', icon: Hourglass, path: '/hourly-work-log', resource: 'hourly_work_management', tooltip: 'Hourly Work log' },
        { name: 'Security Questions', path: '/security-question', tooltip: 'Security Questions', resource: 'password_reset_questions' },
      ],
    },
    {
      name: 'Inventory Category', icon: FolderTree, isDropdown: true, tooltip: 'Inventory Management',
      children: [
        { name: 'Category', path: '/category', tooltip: 'Manage Categories', resource: 'category' },
        { name: 'Subcategory', path: '/subcategory', tooltip: 'Manage Subcategories', resource: 'sub_category' },
        { name: 'Vendor', path: '/vendor', tooltip: 'Manage Vendor', resource: 'vendor' },
        { name: 'Inventory', path: '/inventory', tooltip: 'Manage Inventory', resource: 'inventory' },
        { name: 'Stock', path: '/stock', tooltip: 'Manage Stock', resource: 'stock' },
      ],
    },
    { name: 'Suggestions', icon: NotebookPen, path: '/suggestion', tooltip: 'Suggestions', resource: 'suggestions' },
    { name: 'IT Asset Management', icon: ChartCandlestick, path: '/asset-assign', tooltip: 'Asset Assign', resource: 'asset_assign' },
    {
      name: 'Lead Management', icon: BadgeDollarSign, isDropdown: true, tooltip: 'Lead & Marketing Management',
      children: [
        { name: 'Product Details', icon: PackageSearch, path: '/lead-management/products', resource: 'sales_product', tooltip: 'Manage Products & Variants' },
        { name: 'Messages', icon: MessageCircle, path: '/lead-management/messages', resource: 'sales_message_log', tooltip: 'Send & Track Messages' },
        { name: 'Customer', icon: ContactRound, path: '/lead-management/contacts', resource: 'sales_contact_list', tooltip: 'Manage Contact List' },
        { name: 'Campaigns', icon: FolderOpen, path: '/lead-management/campaigns', resource: 'sales_campaign', tooltip: 'Manage Marketing Campaigns' },
        { name: 'Contact Groups', icon: Users, path: '/lead-management/contact-groups', resource: 'sales_contact_group', tooltip: 'Manage Contact Groups' },
        { name: 'Appointments', icon: CalendarDays, path: '/lead-management/appointments', resource: 'sales_demo_schedule', tooltip: 'Manage Demo Schedules' },
        { name: 'Categories', icon: Tag, path: '/lead-management/categories', resource: 'sales_category', tooltip: 'Manage Contact Categories' },
        { name: 'Lead Status', icon: ListTodo, path: '/lead-management/lead-status', resource: 'lead_status', tooltip: 'Manage Lead Status Options' },
        { name: 'Leads', icon: UsersRound, path: '/lead-management/leads', resource: 'lead', tooltip: 'Manage Sales Leads' },
      ],
    },
    { name: 'Exhibition Management', icon: Landmark, path: '/exhibition-management', resource: 'exhibition', tooltip: 'Exhibition Management' },
    { name: 'New Contact Details', icon: ContactRound, resource: 'new_contact', path: '/contact-details', tooltip: 'New Contact Management' },
    {
      name: 'Product Management', icon: PackageSearch, isDropdown: true, tooltip: 'Product Management',
      children: [
        { name: 'To-Do Categories', icon: FolderTree, path: '/todo-categories', resource: 'tealtik_category', tooltip: 'Manage To-Do Categories' },
        { name: 'Warranty / Guarantee Management', icon: ClockAlert, path: '/warranty-guarantee-management', resource: 'warranty_guarantee', tooltip: 'Warranty / Guarantee Management' },
      ],
    },
    { name: 'My Attachments', icon: Link2, path: '/my-attachments', tooltip: 'My Attachments', resource: 'my_attachment' },
    { name: 'Interview Form', icon: BookOpenCheck, path: '/interview-applications', tooltip: 'Interview Form', resource: 'interview_form' },
    { name: 'Visitor Management', icon: ContactRound, path: '/visitors', tooltip: 'Visitor Management', resource: 'visitor' },
    { name: 'Agency Management', icon: FileText, path: '/agencies', tooltip: 'Agency Agreement Management', resource: 'agency' },
    { name: 'Raise Ticket', icon: Ticket, path: '/raise-ticket', tooltip: 'Raise Ticket', resource: 'ticket' },
    { name: 'Request Issuance', icon: HelpingHand, path: '/request-issuance', tooltip: 'Request Issuance', resource: 'request' },
    {
      name: 'Reports', icon: FileText, isDropdown: true, tooltip: 'Reports', resource: 'reports',
      children: [
        {
          name: 'Inventory Reports', parentName: 'Reports', isDropdown: true, tooltip: 'Inventory Management Reports', resource: 'reports',
          children: [
            { name: 'Current Inventory Summary', path: '/reports/inventory-summary', tooltip: 'Current Inventory Summary Report', resource: 'reports' },
            { name: 'Stock Movement', path: '/reports/stock-movement', tooltip: 'Daily/Weekly/Monthly Stock Movement Report', resource: 'reports' },
            { name: 'Low Stock Alert', path: '/reports/low-stock', tooltip: 'Low Stock Alert Report', resource: 'reports' },
            { name: 'Expired Items', path: '/reports/expired-items', tooltip: 'Expired / Near Expiry Items Report', resource: 'reports' },
            { name: 'Consumption Trend', path: '/reports/consumption-trend', tooltip: 'Item Consumption Trend Report', resource: 'reports' },
            { name: 'Purchase History', path: '/reports/purchase-history', tooltip: 'Purchase History Report', resource: 'reports' },
            { name: 'Price Variation', path: '/reports/price-variation', tooltip: 'Price Variation Report', resource: 'reports' },
          ],
        },
        {
          name: 'Asset Reports', parentName: 'Reports', isDropdown: true, tooltip: 'Asset Management Reports', resource: 'reports',
          children: [
            { name: 'Asset Master', path: '/reports/asset-master', tooltip: 'Asset Master Report', resource: 'reports' },
          ],
        },
        {
          name: 'Ticketing Reports', parentName: 'Reports', isDropdown: true, tooltip: 'Ticketing / Internal Issue Management Reports', resource: 'reports',
          children: [
            { name: 'Tickets Overview', path: '/reports/tickets-overview', tooltip: 'Open / Closed Tickets Report', resource: 'reports' },
            { name: 'TAT Compliance', path: '/reports/tat-compliance', tooltip: 'TAT Compliance Report', resource: 'reports' },
            { name: 'Escalations', path: '/reports/escalations', tooltip: 'Escalation Report', resource: 'reports' },
            { name: 'Department Tickets', path: '/reports/department-tickets', tooltip: 'Department-wise Ticket Volume Report', resource: 'reports' },
            { name: 'Recurring Issues', path: '/reports/recurring-issues', tooltip: 'Recurring Issue Report', resource: 'reports' },
            { name: 'Handler Productivity', path: '/reports/handler-productivity', tooltip: 'Ticket Handler Productivity Report', resource: 'reports' },
          ],
        },
      ],
    },
    {
      name: 'System Logs', icon: Logs, isDropdown: true, tooltip: 'Logs Management',
      children: [
        { name: 'General Log', path: '/general-log', tooltip: 'General Log', resource: 'activity_logs' },
        { name: 'Employee Log', path: '/employee-log', tooltip: 'Employee Log', resource: 'employee_log' },
        { name: 'Permission Log', path: '/permissions-log', tooltip: 'Permission Log', resource: 'permission_log' },
        { name: 'Escalation Log', path: '/escalation-history-log', tooltip: 'Escalation Log', resource: 'escalation_history' },
        { name: 'Inventory Log', path: '/inventory-log', tooltip: 'Inventory Log', resource: 'inventory_log' },
        { name: 'Request Log', path: '/request-logs', tooltip: 'Request Log', resource: 'request_log' },
        { name: 'Asset Assign Log', path: '/asset-assign-logs', tooltip: 'Asset Assign Log', resource: 'asset_assign_log' },
        { name: 'Ticket Log', path: '/ticket-logs', tooltip: 'Ticket Log', resource: 'ticket_log' },
        { name: 'Other Logs', path: '/new-development-log', tooltip: 'New Other Logs', resource: 'activity_logs' },
      ],
    },
    { name: 'Settings', icon: Settings, path: '/settings', tooltip: 'Settings' },
  ];

  const shouldShowDropdown = (item) => {
    if (!item.children) return false;
    const visible = (children) => children.some((c) =>
      !c.resource || hasPermission(c.resource) || (c.children && visible(c.children)));
    return visible(item.children);
  };
  const shouldShowItem = (item) => !item.resource || hasPermission(item.resource);

  return (
    <aside
      aria-label="Main navigation"
      aria-hidden={drawer && !open ? true : undefined}
      style={drawer ? {
        position: 'fixed', left: 0, top: 0, bottom: 0, width: 264, maxWidth: '85vw', zIndex: 50,
        borderRadius: '0 24px 24px 0', overflow: 'hidden',
        background: 'linear-gradient(rgb(94, 20, 30), rgb(62, 14, 22))',
        boxShadow: open ? 'rgba(40, 10, 15, 0.45) 0px 24px 60px 0px' : 'none',
        transform: open ? 'translateX(0)' : 'translateX(-105%)',
        transition: 'transform 220ms ease',
        visibility: open ? 'visible' : 'hidden',
      } : {
        // fixed to the window; the canvas is centred, so its left edge is 50% - 768px
        position: 'fixed', left: 'calc(50% - 748px)', top: 0, width: 196, height: 940, zIndex: 40,
        borderRadius: 28, overflow: 'hidden',
        background: 'linear-gradient(rgb(94, 20, 30), rgb(62, 14, 22))',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        boxShadow: 'rgba(40, 10, 15, 0.35) 0px 24px 60px 0px',
      }}
    >
      {drawer && (
        <button
          type="button" onClick={onClose} aria-label="Close menu"
          style={{ position: 'absolute', right: 10, top: 10, width: 36, height: 36, borderRadius: '50%', border: 0,
            background: 'rgba(255, 255, 255, 0.12)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', zIndex: 1 }}
        >
          <X size={18} />
        </button>
      )}
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 18 }}>
        {/* The logo's wordmark is dark maroon on transparent — invisible straight on this
            dark maroon sidebar, so it sits on a small cream chip instead. */}
        <Link to="/dashboard" aria-label="Dashboard" style={{
          background: 'rgba(252, 248, 242, 0.95)', borderRadius: 14, padding: '8px 14px', display: 'block',
        }}>
          <img src={BrandLogo} alt="Mohilya" width={drawer ? 110 : 136} height={drawer ? 74 : 92} style={{ display: 'block' }} />
        </Link>
      </div>

      <nav
        className="sidebar-scroll"
        style={{
          position: 'absolute', left: 16, right: 16, top: drawer ? 132 : 155, bottom: drawer ? 16 : 262,
          overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5,
        }}
      >
        <TooltipProvider delayDuration={0}>
          {menuItems.map((item) => {
            if (item.isDropdown && !shouldShowDropdown(item)) return null;
            if (!item.isDropdown && !shouldShowItem(item)) return null;
            return (
              <ProtectedSidebarItem
                key={item.name}
                item={item}
                isCollapsed={false}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                iconComponent={AnimatedIcon}
                openReportsSubmenu={openReportsSubmenu}
                setOpenReportsSubmenu={setOpenReportsSubmenu}
              />
            );
          })}
        </TooltipProvider>
      </nav>

      {/* Lotus line-art and tagline, from the design (desktop only) */}
      {!drawer && (<>
      <svg
        viewBox="0 0 200 150" fill="none" stroke="#e8a317" strokeWidth="1.2" aria-hidden="true"
        style={{ position: 'absolute', left: 0, top: 695, width: 196, height: 147, opacity: 0.55 }}
      >
        <path d="M100 140 C60 120,40 90,45 60 C70 70,90 95,100 140z" />
        <path d="M100 140 C140 120,160 90,155 60 C130 70,110 95,100 140z" />
        <path d="M100 140 C80 110,75 80,100 40 C125 80,120 110,100 140z" />
        <path d="M100 140 C50 130,20 100,10 70 C45 75,80 100,100 140z" />
        <path d="M100 140 C150 130,180 100,190 70 C155 75,120 100,100 140z" />
        <path d="M100 140 C30 140,10 120,0 105 C40 105,80 120,100 140z" />
        <path d="M100 140 C170 140,190 120,200 105 C160 105,120 120,100 140z" />
      </svg>
      <div
        style={{
          position: 'absolute', left: 20, top: 824, fontFamily: 'var(--font-heading)',
          fontSize: 18, lineHeight: 1.2, color: 'rgb(255, 233, 214)',
        }}
      >
        Designing<br />Tradition<br />Stitching<br />Tomorrow
      </div>
      <div style={{ position: 'absolute', left: 20, top: 918, width: 40, height: 2, background: 'rgb(232, 163, 23)' }} />
      </>)}
    </aside>
  );
};

export default Sidebar;
